import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Spinner, PageSkeleton } from '@daycal/ui';
import { FiUsers, FiSearch, FiShield, FiKey, FiLock, FiUnlock, FiCheck, FiX, FiCalendar, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { LuInfinity } from 'react-icons/lu';
import { api } from '../../lib/apiClient.js';

function daysUntil(iso) {
  return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000));
}

function UserRow({ u, plans, freePlanId }) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin-users'] });
  const disable = useMutation({ mutationFn: () => api.adminDisableUser(u.id), onSuccess: invalidate });
  const enable = useMutation({ mutationFn: () => api.adminEnableUser(u.id), onSuccess: invalidate });
  const [planMsg, setPlanMsg] = useState('');
  const changePlan = useMutation({
    mutationFn: ({ planId, validDays }) => api.adminChangePlan(u.id, planId, validDays),
    onSuccess: () => { setPlanMsg('Plan updated'); invalidate(); },
    onError: (err) => setPlanMsg(err.message),
  });

  const [planId, setPlanId] = useState(u.plan_id || '');
  const [days, setDays] = useState(u.plan_valid_until ? String(daysUntil(u.plan_valid_until)) : '');
  const isFreeSelected = planId && planId === freePlanId;
  const lifetimePlan = plans.find((p) => p.is_lifetime);

  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState('');
  const [pwMsg, setPwMsg] = useState('');
  const resetPw = useMutation({
    mutationFn: () => api.adminResetPassword(u.id, pw),
    onSuccess: () => { setPwMsg('Password updated'); setPw(''); setPwOpen(false); },
    onError: (err) => setPwMsg(err.message),
  });

  const isProtected = u.is_primary_super_admin;
  const initial = (u.name || u.username || '?').charAt(0).toUpperCase();

  return (
    <div className="rounded-dc border border-slate-200/80 bg-white p-5 shadow-xs transition-all hover:shadow-md space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        {/* User Info & Avatar */}
        <div className="flex items-center gap-3.5 min-w-[240px]">
          <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-slate-700 font-extrabold text-white text-base shadow-2xs">
            {initial}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-display font-extrabold text-slate-900 text-sm">
                {u.name || u.username}
              </span>
              <span className="font-mono text-xs text-slate-400">@{u.username}</span>
            </div>
            <div className="text-xs text-slate-500 font-medium">{u.email}</div>
          </div>
        </div>

        {/* Action Controls & Badges */}
        <div className="flex flex-wrap items-center gap-2.5">
          {/* Status Badge */}
          <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-bold shadow-2xs border ${
            u.status === 'active' 
              ? 'bg-emerald-50 border-emerald-200 text-emerald-700' 
              : 'bg-rose-50 border-rose-200 text-rose-700'
          }`}>
            <span className={`h-1.5 w-1.5 rounded-full ${u.status === 'active' ? 'bg-emerald-500' : 'bg-rose-500'}`} />
            {u.status}
          </span>

          {/* Expiry Badge */}
          {u.plan_valid_until ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2.5 py-0.5 text-xs font-bold text-amber-700 shadow-2xs">
              <FiCalendar className="h-3 w-3" />
              Expires {new Date(u.plan_valid_until).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} ({daysUntil(u.plan_valid_until)}d left)
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full bg-slate-100 border border-slate-200/80 px-2.5 py-0.5 text-xs font-bold text-slate-600">
              Permanent
            </span>
          )}

          {/* Plan Form Selector */}
          <div className="flex items-center gap-1.5" title={isProtected ? 'The primary Super Admin cannot be demoted' : undefined}>
            <CustomSelect
              options={plans.map(p => ({ value: p.id, label: p.name }))}
              value={planId}
              onChange={(val) => { setPlanId(val); if (val === freePlanId) setDays(''); }}
              disabled={isProtected}
              placeholder="Select Plan"
            />
            <input
              type="number"
              min="0"
              max="3650"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              disabled={isProtected || isFreeSelected}
              placeholder="days"
              title={isFreeSelected ? 'The Free plan is permanent' : 'Validity in days (blank = permanent)'}
              className="h-9 w-16 rounded-dc-sm border border-slate-300/90 bg-white px-2.5 text-xs font-semibold text-slate-800 outline-none transition-all hover:border-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/10 disabled:bg-slate-50 disabled:text-slate-400 shadow-2xs text-center"
            />
            <button
              type="button"
              disabled={isProtected || !planId || changePlan.isPending}
              onClick={() => { setPlanMsg(''); changePlan.mutate({ planId, validDays: Number(days) || 0 }); }}
              className="h-9 rounded-dc-sm bg-slate-900 px-3 text-xs font-bold text-white shadow-xs hover:bg-brand-600 active:scale-95 disabled:opacity-40 transition-all cursor-pointer"
            >
              {changePlan.isPending ? <Spinner className="!h-3.5 !w-3.5 !border-t-white" /> : 'Apply'}
            </button>
          </div>

          {/* Grant Lifetime (assigns the Lifetime plan permanently — after verifying payment) */}
          {lifetimePlan && !isProtected && (
            <button
              type="button"
              disabled={changePlan.isPending}
              onClick={() => { setPlanMsg(''); changePlan.mutate({ planId: lifetimePlan.id, validDays: 0 }); }}
              title="Assign the Lifetime plan permanently (after verifying the customer's payment)"
              className="flex h-9 items-center gap-1.5 rounded-dc-sm border border-brand-300 bg-brand-50 px-3 text-xs font-bold text-brand-700 shadow-2xs hover:bg-brand-600 hover:text-white hover:border-brand-600 active:scale-95 disabled:opacity-40 transition-all cursor-pointer"
            >
              <LuInfinity className="h-3.5 w-3.5" />
              <span>Grant Lifetime</span>
            </button>
          )}

          {/* Password Action */}
          <button
            type="button"
            disabled={isProtected}
            onClick={() => { setPwOpen((o) => !o); setPwMsg(''); }}
            className="flex h-9 items-center gap-1.5 rounded-dc-sm border border-slate-300/90 bg-white px-3 text-xs font-bold text-slate-700 shadow-2xs hover:bg-slate-50 active:scale-95 disabled:opacity-40 transition-all cursor-pointer"
          >
            <FiKey className="h-3.5 w-3.5 text-slate-500" />
            <span>Password</span>
          </button>

          {/* Disable / Enable Action */}
          {isProtected ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 border border-indigo-200 px-2.5 py-0.5 text-xs font-bold text-indigo-700">
              <FiShield className="h-3 w-3" /> Protected
            </span>
          ) : u.status === 'active' ? (
            <button
              type="button"
              onClick={() => disable.mutate()}
              disabled={disable.isPending}
              className="flex h-9 items-center gap-1.5 rounded-dc-sm bg-rose-600 px-3.5 text-xs font-bold text-white shadow-2xs hover:bg-rose-700 active:scale-95 disabled:opacity-50 transition-all cursor-pointer"
            >
              <FiLock className="h-3.5 w-3.5" />
              <span>Disable</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={() => enable.mutate()}
              disabled={enable.isPending}
              className="flex h-9 items-center gap-1.5 rounded-dc-sm bg-emerald-600 px-3.5 text-xs font-bold text-white shadow-2xs hover:bg-emerald-700 active:scale-95 disabled:opacity-50 transition-all cursor-pointer"
            >
              <FiUnlock className="h-3.5 w-3.5" />
              <span>Enable</span>
            </button>
          )}
        </div>
      </div>

      {planMsg && (
        <p className={`text-xs font-semibold ${changePlan.isError ? 'text-rose-600' : 'text-emerald-700'}`}>{planMsg}</p>
      )}

      {/* Password Reset Form Drawer */}
      {pwOpen && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3 animate-dc-rise">
          <div className="relative min-w-[220px] flex-1">
            <input
              type="password"
              placeholder="New password (at least 8 characters)"
              value={pw}
              onChange={(e) => { setPw(e.target.value); setPwMsg(''); }}
              className="w-full rounded-dc-sm border border-slate-300/90 bg-white px-3.5 py-2 text-xs font-semibold text-slate-800 outline-none transition-all placeholder:text-slate-400 hover:border-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/10 shadow-2xs"
            />
          </div>
          <button
            type="button"
            onClick={() => resetPw.mutate()}
            disabled={resetPw.isPending || pw.length < 8}
            className="flex h-9 items-center justify-center rounded-dc-sm bg-slate-900 px-4 text-xs font-bold text-white shadow-xs hover:bg-brand-600 active:scale-95 disabled:opacity-40 transition-all cursor-pointer"
          >
            {resetPw.isPending ? <Spinner className="!h-3.5 !w-3.5 !border-t-white" /> : 'Set Password'}
          </button>
          <button
            type="button"
            onClick={() => { setPwOpen(false); setPw(''); setPwMsg(''); }}
            className="flex h-9 items-center justify-center rounded-dc-sm border border-slate-300/90 bg-white px-3 text-xs font-bold text-slate-700 shadow-2xs hover:bg-slate-50 cursor-pointer active:scale-95 transition-all"
          >
            Cancel
          </button>
        </div>
      )}
      {pwMsg && <p className="text-xs font-semibold text-slate-500">{pwMsg}</p>}
    </div>
  );
}

const PER_PAGE = 10;

export default function AdminUsers() {
  const [search, setSearch] = useState(''); // raw input
  const [q, setQ] = useState(''); // debounced term actually sent to the server
  const [page, setPage] = useState(1);

  // Debounce the search box → server query, and jump back to page 1 on every new term.
  useEffect(() => {
    const t = setTimeout(() => {
      setQ(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-users', page, q],
    queryFn: () =>
      api.adminListUsers(`?page=${page}&per_page=${PER_PAGE}${q ? `&q=${encodeURIComponent(q)}` : ''}`),
    placeholderData: (prev) => prev, // keep the current page on screen while the next loads
  });
  const { data: plansData } = useQuery({ queryKey: ['plans'], queryFn: api.listPlans });

  const users = data?.users || [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const plans = plansData?.plans || [];
  const freePlanId = plans[0]?.id;

  // If the roster shrank (e.g. after deletions) never strand on a now-empty trailing page.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  if (isLoading) return <PageSkeleton />;

  return (
    <div className="animate-dc-rise space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-200/80 pb-5">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="font-display text-2xl font-extrabold text-slate-900">User Management</h1>
            <span className="rounded-full bg-slate-900 px-2.5 py-0.5 text-xs font-bold text-white shadow-xs">
              {total} Users
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Manage user accounts, plan assignments, validity periods, and system access.
          </p>
        </div>

        {/* Search Input */}
        <div className="relative min-w-[240px]">
          <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search users..."
            className="w-full rounded-dc-sm border border-slate-300/90 bg-white pl-9 pr-3.5 py-2 text-xs font-semibold text-slate-800 outline-none transition-all placeholder:text-slate-400 hover:border-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/10 shadow-2xs"
          />
        </div>
      </div>

      {/* Users List */}
      <div className="grid gap-3.5">
        {users.length === 0 ? (
          <div className="rounded-dc border border-dashed border-slate-200/80 bg-white p-8 text-center">
            <FiUsers className="mx-auto h-8 w-8 text-slate-300 mb-2" />
            <p className="text-xs font-bold text-slate-600">
              {q ? 'No matching users found' : 'No users yet'}
            </p>
          </div>
        ) : (
          users.map((u) => <UserRow key={u.id} u={u} plans={plans} freePlanId={freePlanId} />)
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-slate-200/80 pt-4">
          <span className="text-xs font-semibold text-slate-500">
            Page {page} of {totalPages} · {total} total
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="flex items-center gap-1.5 rounded-dc-sm border border-slate-300/90 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 shadow-2xs transition-all hover:bg-slate-50 active:scale-95 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <FiChevronLeft className="h-3.5 w-3.5" /> Prev
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="flex items-center gap-1.5 rounded-dc-sm border border-slate-300/90 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 shadow-2xs transition-all hover:bg-slate-50 active:scale-95 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next <FiChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Click-outside CustomSelect
function CustomSelect({ options, value, onChange, disabled, placeholder }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const selectedLabel = options.find(o => String(o.value) === String(value))?.label || placeholder || 'Select...';

  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(!open)}
        className={`w-32 h-9 flex items-center justify-between rounded-dc-sm border border-slate-300/90 px-3 text-xs font-bold outline-none transition-all shadow-2xs cursor-pointer ${
          disabled ? 'bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed' : 'bg-white text-slate-800 hover:border-slate-400'
        }`}
      >
        <span className="truncate">{selectedLabel}</span>
        <svg className={`ml-1.5 h-3.5 w-3.5 shrink-0 transition-transform ${open ? 'rotate-180 text-slate-900' : 'text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {open && !disabled && (
        <div className="absolute right-0 z-30 mt-1 w-36 rounded-dc-sm border border-slate-200/80 bg-white py-1 shadow-md animate-dc-rise max-h-52 overflow-y-auto custom-scrollbar">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
              className={`w-full px-3 py-1.5 text-left text-xs transition-colors hover:bg-slate-900 hover:text-white cursor-pointer ${
                String(value) === String(opt.value) ? 'bg-slate-100 font-extrabold text-slate-900' : 'text-slate-700 font-semibold'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
